import { useCallback, useRef } from "react";

export type Compass = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";

export const Ordinals: { [key: string]: string } = {
  n: "bottom",
  e: "left",
  s: "top",
  w: "right",
};

interface Positioning {
  direction: Compass;
  isResizing: boolean;
  startX: number;
  startY: number;
  startProportionOfMaxWidth: number;
  startWidth: number;
  startHeight: number;
}

const minWidth = 100;
const minHeight = 100;

export function useImageResizer({
  onResizeStart,
  onResizeEnd,
  proportionOfMaxWidth,
  maxWidth,
  maxHeight = 10_000,
}: {
  onResizeStart: () => void;
  onResizeEnd: (proportionOfMaxWidth: number) => void;
  proportionOfMaxWidth: number;
  maxWidth?: number;
  maxHeight?: number;
}): {
  positioningRef: React.RefObject<Positioning>;
  controlWrapperRef: React.RefObject<HTMLDivElement | null>;
  handlePointerDown: (event: React.PointerEvent<HTMLDivElement>, direction: Compass) => void;
} {
  const controlWrapperRef = useRef<HTMLDivElement>(null);
  const positioningRef = useRef<Positioning>({
    direction: "n",
    isResizing: false,
    startX: 0,
    startY: 0,
    startProportionOfMaxWidth: 0,
    startWidth: 0,
    startHeight: 0,
  });

  const handlePointerMove = useCallback(
    (event: PointerEvent): void => {
      const positioning = positioningRef.current;

      const softMaxWidth =
        maxWidth ?? positioning.startWidth / positioning.startProportionOfMaxWidth;

      const isHorizontal =
        positioning.direction.includes("w") || positioning.direction.includes("e");
      const isVertical = positioning.direction.includes("s") || positioning.direction.includes("n");

      if (controlWrapperRef.current && positioning.isResizing) {
        let requestedWidth = positioning.startWidth;
        let requestedHeight = positioning.startHeight;
        if (isVertical) {
          requestedHeight += positioning.direction.includes("s")
            ? event.clientY - positioning.startY
            : positioning.startY - event.clientY;
        }
        if (isHorizontal) {
          requestedWidth += positioning.direction.includes("e")
            ? event.clientX - positioning.startX
            : positioning.startX - event.clientX;
        }
        requestedWidth = Math.min(Math.max(minWidth, requestedWidth), softMaxWidth);
        requestedHeight = Math.min(Math.max(minHeight, requestedHeight), maxHeight);
        if (isVertical && isHorizontal) {
          const scale = Math.min(
            requestedWidth / positioning.startWidth,
            requestedHeight / positioning.startHeight,
          );
          requestedWidth = positioning.startWidth * scale;
        }
        controlWrapperRef.current.style.width = `${(requestedWidth / softMaxWidth) * 100}%`;
      }
    },
    [maxWidth, maxHeight],
  );

  const handlePointerUp = useCallback((): void => {
    if (controlWrapperRef.current && positioningRef.current.isResizing) {
      positioningRef.current.isResizing = false;
      if (document.body !== null) {
        document.body.style.setProperty("cursor", "default");
        document.body.style.setProperty("user-select", "");
      }
      if (controlWrapperRef.current) controlWrapperRef.current.style.touchAction = "";
      onResizeEnd(Number(controlWrapperRef.current.style.width.slice(0, -1)) / 100);
      document.removeEventListener("pointermove", handlePointerMove);
    }
  }, [onResizeEnd, handlePointerMove]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, direction: Compass): void => {
      const positioning = positioningRef.current;
      if (controlWrapperRef.current) {
        positioning.startX = event.clientX;
        positioning.startY = event.clientY;
        positioning.isResizing = true;
        positioning.direction = direction;
        positioning.startProportionOfMaxWidth = proportionOfMaxWidth;
        positioning.startWidth = controlWrapperRef.current.clientWidth;
        positioning.startHeight = controlWrapperRef.current.clientHeight;
        if (document.body !== null) {
          document.body.style.setProperty("cursor", `${direction}-resize`, "important");
          document.body.style.setProperty("user-select", `none`, "important");
        }
        document.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", handlePointerUp, { once: true });
        onResizeStart();
      }
    },
    [proportionOfMaxWidth, handlePointerUp, handlePointerMove, onResizeStart],
  );

  return {
    positioningRef,
    controlWrapperRef,
    handlePointerDown,
  };
}
